import { Fragment, useMemo } from "react";
import { Button, OverlayTrigger, Popover, Table } from "react-bootstrap";
import {
  Check,
  ChevronDown,
  ChevronRight,
  InfoCircle,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { Currency } from "../../../../lib/components/Currency";
import { EmptyRow } from "./EmptyState";
import { RecordActions } from "./RecordActions";
import { CommissionDetailsRow } from "./CommissionDetailsRow";
import {
  buildContractSelectionRow,
  getContractSelectionKey,
  isContractSelectable,
} from "../utils/recordSelection";

const COL_COUNT = 7;

function isRejectedStatus(status) {
  return status === "rejected" || status === "declined";
}

function statusClass(status) {
  if (status === "approved") return "status-text status-text--approved";
  if (status === "paid") return "status-text status-text--paid";
  if (status === "pending" || status === "in_review")
    return "status-text status-text--pending";
  if (isRejectedStatus(status)) return "status-text status-text--rejected";
  return "status-text";
}

function statusLabel(status) {
  if (!status) return "-";
  if (isRejectedStatus(status)) return "Declined";
  return status;
}

export function CommissionsSubTable({
  contracts,
  agentKey,
  expandedRows,
  onToggleRow,
  onAdjust,
  onReject,
  onApprove,
  onRemove,
  onReopen,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  isSelectionDisabled,
  areRecordActionsDisabled,
  approvingRecordKeys,
  removingRecordKeys,
}) {
  const selectionRows = useMemo(
    () => contracts.map((ac, i) => buildContractSelectionRow(agentKey, ac, i)),
    [agentKey, contracts],
  );

  const selectableRows = selectionRows.filter((r) => r.selectable);
  const selectableCount = selectableRows.length;
  const selectedCount = selectableRows.filter((r) =>
    selectedKeys.has(r.key),
  ).length;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = () => onToggleSelectAll(selectableRows, !allSelected);

  return (
    <>
      <Table
        hover
        responsive
        className="data-table data-table--interactive align-middle"
        striped
      >
        {contracts.length > 0 && (
          <thead>
            <tr>
              <th className="col-select" aria-label="Select all">
                {selectableCount > 0 && (
                  <button
                    type="button"
                    role="checkbox"
                    className={`record-select-checkbox ${allSelected || someSelected ? "is-on" : ""}`.trim()}
                    aria-label="Select all rows"
                    aria-checked={someSelected ? "mixed" : allSelected}
                    onClick={toggleAll}
                    disabled={selectableCount === 0 || isSelectionDisabled}
                  >
                    {someSelected ? (
                      <span className="record-select-dash" />
                    ) : allSelected ? (
                      <Check size={11} strokeWidth={3.2} />
                    ) : null}
                  </button>
                )}
              </th>
              <th className="col-toggle" aria-label="Details" />
              <th>Provider #</th>
              <th>Contract #</th>
              <th>Status</th>
              <th className="col-money">Amount</th>
              <th className="col-actions col-sticky">Actions</th>
            </tr>
          </thead>
        )}
        <tbody>
          {contracts.length === 0 ? (
            <EmptyRow colSpan={COL_COUNT} text="No commission records." />
          ) : (
            contracts.map((ac, i) => {
              const rowKey = `${agentKey}-contract-${ac.commissionAgentContractId ?? i}`;
              const firstRecord = ac.commissionRecords[0];
              const itemRecords = ac.commissionRecords.flatMap(
                (r) => r.itemRecords,
              );
              const contractAdjustmentTotal = ac.contractAdjustments.reduce(
                (s, a) => s + (a.adjustmentAmount ?? 0),
                0,
              );
              const baseCommissionAmount = ac.commissionRecords.reduce(
                (s, r) => s + (r.amount ?? 0),
                0,
              );
              const rowAmount = baseCommissionAmount + contractAdjustmentTotal;
              const hasDetails =
                itemRecords.length > 0 ||
                ac.contractAdjustments.length > 0 ||
                ac.commissionRecords.length > 0 ||
                Boolean(ac.notes?.trim());
              const isExpanded = expandedRows.has(rowKey);
              const isFinalized =
                ac.status === "approved" || ac.status === "declined";
              const contractNumber = firstRecord?.contractNumber;
              const recordActionKey = `commission:${ac.agentId || ""}:${ac.commissionAgentContractId || contractNumber || i}`;
              const providerNumber = firstRecord?.providerNumber;
              const label = `Contract #${contractNumber || ac.commissionAgentContractId}`;
              const showAmountPopover =
                ac.contractAdjustments.length > 0 ||
                contractAdjustmentTotal !== 0;
              const popoverId = `contract-amount-breakdown-${ac.commissionAgentContractId ?? i}`;
              const selectionId = getContractSelectionKey(agentKey, ac, i);
              const isSelected = selectedKeys.has(selectionId);
              const isSelectable = isContractSelectable(ac);

              return (
                <Fragment key={rowKey}>
                  <tr>
                    <td className="col-select">
                      <button
                        type="button"
                        role="checkbox"
                        className={`record-select-checkbox ${isSelected ? "is-on" : ""} ${!isSelectable ? "is-disabled" : ""}`.trim()}
                        aria-label={`Select contract ${contractNumber || ac.commissionAgentContractId || ""}`}
                        aria-checked={isSelected}
                        onClick={() =>
                          onToggleSelect(
                            buildContractSelectionRow(agentKey, ac, i),
                          )
                        }
                        disabled={!isSelectable || isSelectionDisabled}
                      >
                        {isSelected ? (
                          <Check size={11} strokeWidth={3.2} />
                        ) : null}
                      </button>
                    </td>
                    <td className="col-toggle">
                      {hasDetails && (
                        <Button
                          variant="link"
                          className="btn-toggle"
                          onClick={() => onToggleRow(rowKey)}
                          aria-label={
                            isExpanded ? "Collapse details" : "Expand details"
                          }
                        >
                          {isExpanded ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronRight size={14} />
                          )}
                        </Button>
                      )}
                    </td>
                    <td>
                      {contractNumber && providerNumber ? (
                        <Link
                          to={`/contracts/${contractNumber}`}
                          target="_blank"
                        >
                          {providerNumber}
                        </Link>
                      ) : (
                        providerNumber || "-"
                      )}
                    </td>
                    <td>
                      {contractNumber ? (
                        <Link
                          to={`/contracts/${contractNumber}`}
                          target="_blank"
                        >
                          {contractNumber}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <span className={statusClass(ac.status)}>
                        {statusLabel(ac.status)}
                      </span>
                    </td>
                    <td className="col-money tnum col-money--emphasis">
                      {showAmountPopover ? (
                        <span className="bonus-amount-wrap">
                          <Currency amount={rowAmount} />
                          <OverlayTrigger
                            placement="top"
                            trigger={["hover", "focus"]}
                            overlay={
                              <Popover
                                id={popoverId}
                                className="bonus-amount-popover"
                              >
                                <Popover.Body>
                                  <div className="bonus-amount-tooltip-grid">
                                    <span>Base commission</span>
                                    <span>
                                      <Currency amount={baseCommissionAmount} />
                                    </span>
                                    <span>Contract adjustments</span>
                                    <span>
                                      <Currency
                                        amount={contractAdjustmentTotal}
                                      />
                                    </span>
                                  </div>
                                  <div className="bonus-amount-tooltip-divider" />
                                  <div className="bonus-amount-tooltip-grid bonus-amount-tooltip-total">
                                    <span>Total</span>
                                    <span>
                                      <Currency amount={rowAmount} />
                                    </span>
                                  </div>
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <button
                              type="button"
                              className="bonus-amount-trigger"
                              aria-label="Show contract amount breakdown"
                            >
                              <InfoCircle size={13} aria-hidden="true" />
                            </button>
                          </OverlayTrigger>
                        </span>
                      ) : (
                        <span className="bonus-amount-wrap">
                          <Currency amount={rowAmount} />
                          <span
                            className="bonus-amount-trigger-spacer"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </td>
                    <td className="col-actions col-sticky">
                      <RecordActions
                        isFinalized={isFinalized}
                        isApproving={approvingRecordKeys.has(recordActionKey)}
                        isRemoving={removingRecordKeys.has(recordActionKey)}
                        isDisabled={areRecordActionsDisabled}
                        onApprove={
                          onApprove
                            ? () =>
                                onApprove({
                                  kind: "commission",
                                  id: ac.commissionAgentContractId,
                                  agentId: ac.agentId,
                                  contractNumber,
                                  label,
                                })
                            : undefined
                        }
                        onAdjust={() =>
                          onAdjust({
                            kind: "commission",
                            id: ac.commissionAgentContractId,
                            agentId: ac.agentId,
                            contractNumber,
                            label,
                            effective: rowAmount,
                          })
                        }
                        onReject={() =>
                          onReject({
                            kind: "commission",
                            id: ac.commissionAgentContractId,
                            agentId: ac.agentId,
                            contractNumber,
                            label,
                          })
                        }
                        onRemove={
                          onRemove
                            ? () =>
                                onRemove({
                                  kind: "commission",
                                  id: ac.commissionAgentContractId,
                                  agentId: ac.agentId,
                                  contractNumber,
                                  label,
                                })
                            : undefined
                        }
                        onReopen={
                          onReopen
                            ? () =>
                                onReopen({
                                  kind: "commission",
                                  id: ac.commissionAgentContractId,
                                  agentId: ac.agentId,
                                  contractNumber,
                                  label,
                                })
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                  {isExpanded && hasDetails && (
                    <CommissionDetailsRow
                      colSpan={COL_COUNT}
                      commissionRecords={ac.commissionRecords}
                      itemRecords={itemRecords}
                      contractAdjustments={ac.contractAdjustments}
                      notes={ac.notes}
                    />
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </Table>
    </>
  );
}
